import { validationMessages } from "../../../api/genericData";
import dateUtil from "../../../lib/dateUtil";

type ValidationMessages = Record<string, string>;

type SetValidationMessages = (messages: ValidationMessages) => void;

type ValidatorFunction<T> = (value: T) => string | undefined;

export type Validators<T> = ValidatorFunction<T> | ValidatorFunction<T>[];

const emailAddress =
  /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
const mobileNumber = /^(((\+31|0|0031)6[-]?){1}[1-9]{1}[0-9]{7})$/;
const telephoneNumber = /^(((0)[1-9]{2}[0-9][-]?[1-9][0-9]{5})|((\+31|0|0031)[1-9][0-9][-]?[1-9][0-9]{6}))$/;

let messages: ValidationMessages = {};
if (typeof window !== "undefined" && Object.keys(messages).length === 0) {
  const ssrRawJson = document.getElementById("__JSS_STATICDATA__");
  // SSR rendering viewbag is visible, then use the viewbag to fill the enumValue
  // For connected mode we use the AJAX call to fill the enumValue
  if (ssrRawJson) {
    const __JSS_STATICDATA__ = JSON.parse(ssrRawJson.innerHTML) as Record<string, unknown>;
    if (__JSS_STATICDATA__ && __JSS_STATICDATA__.validationMessages) {
      messages = __JSS_STATICDATA__.validationMessages as ValidationMessages;
    } else {
      getValidationMessages((response) => {
        messages = response;
      });
    }
  }
}

function getValidationMessages(setMessages: SetValidationMessages) {
  executeValidationMessages(setMessages).catch(() => null);
}

async function executeValidationMessages(setMessages: SetValidationMessages) {
  try {
    const retrievedMessages = await validationMessages();
    setMessages(retrievedMessages);
  } catch (e) {
    console.error(`Failed to load messages: ${e}`);
  }
}

export function storeValidationMessages(content?: ValidationMessages) {
  if (content) {
    messages = content;
  }
}

export const V1 = (input: string | number) => {
  if (/\d+/.test(`${input}`)) {
    return undefined;
  }

  return messages["V1"];
};

export const V3 = (input: string) => {
  if (/^[A-Za-z]+$/.test(input)) {
    return undefined;
  }

  return messages["V3"];
};

export const V4 = (input: string) => {
  if (/^[A-Za-z0-9]+$/.test(input)) {
    return undefined;
  }

  return messages["V4"];
};

export const V5 = (input: string | number) => {
  if (!input) {
    return messages["V5"];
  }

  return undefined;
};

export const V6 = (input: string) => {
  const moment = dateUtil.strictDutchDateToMoment(input);
  if (input !== "" && !moment.isValid()) {
    return messages["V6"];
  }

  return undefined;
};

export const V7 = (input?: string) => {
  if (!input) {
    return messages["V7"];
  }

  return undefined;
};

export const V10 = (maxLength: number) => {
  return (input: string) => {
    if (input.length > maxLength) {
      return messages["V10"].replace("{0}", `${maxLength}`);
    }

    return undefined;
  };
};

export const V11 = (minLength: number) => {
  return (input: string) => {
    if (input.length < minLength) {
      return messages["V11"].replace("{0}", `${minLength}`);
    }

    return undefined;
  };
};

export const V12 = (input: string) => {
  if (emailAddress.test(input)) {
    return undefined;
  }

  return messages["V12"];
};

export const V13 = (minValue: number) => {
  return (input: number) => {
    if (input < minValue) {
      return messages["V13"].replace("{0}", `${minValue}`);
    }

    return undefined;
  };
};

export const V14 = (maxValue: number) => {
  return (input: number) => {
    if (input > maxValue) {
      return messages["V14"].replace("{0}", `${maxValue}`);
    }

    return undefined;
  };
};

export const V21 = (input: string) => {
  if (/^[1-9][0-9]{3}\s?[a-zA-Z]{2}$/i.test(input)) {
    return undefined;
  }

  return messages["V21"];
};

export const V22 = (input: string) => {
  if (/\d/.test(input)) {
    return messages["V22"];
  }

  return undefined;
};

export const V24 = (input: string) => {
  if (mobileNumber.test(input) || telephoneNumber.test(input)) {
    return undefined;
  }

  return messages["V24"];
};

export const V25 = (value: string) => {
  const iban = value.replace(/[^A-Z0-9]+/gi, "").toUpperCase();
  if (iban.length <= 4) {
    return messages["V25"];
  }

  if (iban.startsWith("NL") && iban.length !== 18) {
    return messages["V25"];
  }

  const m = /^([A-Z]{2})([0-9]{2})([A-Z0-9]{9,30})$/.exec(iban);

  if (!m) {
    return messages["V25"];
  }

  return undefined;
};

export const V45 = (minValue: number, maxValue: number) => {
  return (input: number) => {
    if (input < minValue || input > maxValue) {
      return messages["V45"].replace("{0}", `${minValue}`).replace("{1}", `${maxValue}`);
    }

    return undefined;
  };
};

export const BR208 = (maxValue: number) => {
  return (input: number) => {
    if (input > maxValue) {
      return messages["M20"].replace("{0}", `${maxValue}`);
    }

    return undefined;
  };
};

export const BR209 = (minValue: number) => {
  return (input: number) => {
    if (input < minValue) {
      return messages["M21"].replace("{0}", `${minValue}`);
    }

    return undefined;
  };
};

export const BR301 = (maxValue: number) => {
  return (input: number) => {
    if (input > maxValue) {
      return messages["M25"];
    }

    return undefined;
  };
};

export const BR302 = (minValue: number) => {
  return (input: number) => {
    if (input < minValue) {
      return messages["M26"];
    }

    return undefined;
  };
};
